import { personFilesResponseSchema, type FileContentQuery, type PersonFilesResponse } from '@vigeo/shared';
import { fixtureMode } from '../../lib/data-mode.js';

/**
 * Why a file request came back empty-handed. The API answers 404 for every refusal — another
 * person's file, a missing one, one this viewer may not see — so the UI cannot tell them apart
 * and must not try to (ADR-009 §4).
 */
export type FileProblem = 'not-found' | 'unavailable' | 'failed';

export class FileRequestError extends Error {
  constructor(readonly problem: FileProblem) {
    super(problem === 'not-found' ? 'Not found' : problem === 'unavailable' ? 'Unavailable' : 'Failed');
  }
}

function problemFor(status: number): FileProblem {
  return status === 404 ? 'not-found' : status === 503 ? 'unavailable' : 'failed';
}

/**
 * Every path the UI builds. Ids only: a person's name, a document label or an original file
 * name never appears in a URL (CLAUDE.md rule 8), and these are fetch targets — never a link
 * the browser navigates to, so nothing lands in the address bar or history.
 */
export const filePaths = {
  list: (personId: string) => `/api/people/${encodeURIComponent(personId)}/files`,
  content: (personId: string, fileId: string, disposition: FileContentQuery['disposition']) =>
    `/api/people/${encodeURIComponent(personId)}/files/${encodeURIComponent(fileId)}/content?disposition=${disposition}`,
};

/** Fixtures reach the app only through this guarded import; `fixtureMode` is `false` in a build. */
async function fixtures() {
  return import('../../../dev-fixtures/files.js');
}

export async function fetchPersonFiles(personId: string): Promise<PersonFilesResponse> {
  if (fixtureMode) return (await fixtures()).fixturePersonFiles(personId);
  const response = await fetch(filePaths.list(personId), { cache: 'no-store' });
  if (!response.ok) throw new FileRequestError(problemFor(response.status));
  return personFilesResponseSchema.parse(await response.json());
}

export interface FileContent {
  readonly bytes: ArrayBuffer;
  /** From the response, which the server takes from the stored allowlisted type. */
  readonly contentType: string;
  /** The server-built download name from `Content-Disposition`, or null if absent. */
  readonly fileName: string | null;
}

/** RFC 5987 `filename*` first — the exact name — then the ASCII `filename` fallback. */
export function fileNameFromDisposition(header: string | null): string | null {
  if (header === null) return null;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1] !== undefined) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // Fall through to the plain form.
    }
  }
  const plain = /filename="([^"]*)"/i.exec(header);
  return plain?.[1] ?? null;
}

/**
 * The bytes of one file, fetched through the API proxy. Deliberately not a TanStack query: no
 * file content is cached client-side beyond the viewer that asked for it. Every call is
 * a fresh, audited read.
 */
export async function fetchFileContent(
  personId: string,
  fileId: string,
  disposition: FileContentQuery['disposition'],
  signal?: AbortSignal,
): Promise<FileContent> {
  if (fixtureMode) {
    const content = (await fixtures()).fixtureFileContent(personId, fileId);
    if (content === null) throw new FileRequestError('not-found');
    return content;
  }
  const response = await fetch(filePaths.content(personId, fileId, disposition), { cache: 'no-store', signal });
  if (!response.ok) throw new FileRequestError(problemFor(response.status));
  return {
    bytes: await response.arrayBuffer(),
    contentType: (response.headers.get('content-type') ?? '').split(';')[0]!.trim().toLowerCase(),
    fileName: fileNameFromDisposition(response.headers.get('content-disposition')),
  };
}
